import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";

import { IRagConfig } from "../../@types/Rag";
import { ragConfigService } from "../../services/ragConfigService";

type ApiError = Error & { response?: AxiosResponse };

function ensureOk<T>(response: AxiosResponse<T>, action: string): T {
  if (response.status < 200 || response.status >= 300) {
    const err: ApiError = new Error(
      `RAG ${action} failed with status ${response.status}`
    );
    err.response = response;
    throw err;
  }
  return response.data;
}

function invalidatePatientListCaches(
  queryClient: ReturnType<typeof useQueryClient>
) {
  queryClient.invalidateQueries({ queryKey: ["patient-list-active"] });
  queryClient.invalidateQueries({ queryKey: ["patient-list-flagged"] });
  queryClient.invalidateQueries({ queryKey: ["patient-list-alerts"] });
  queryClient.invalidateQueries({ queryKey: ["patient-list-archived"] });
}

export function useCreateRagConfig(hospitalGroupId: number | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: IRagConfig) => {
      if (!hospitalGroupId) throw new Error("Missing hospitalGroupId");
      const response = await ragConfigService.create(hospitalGroupId, config);
      return ensureOk(response, "create");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["rag-config-list", hospitalGroupId],
      });
      invalidatePatientListCaches(queryClient);
    },
  });
}

export function useUpdateRagConfig(id: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: IRagConfig) => {
      if (!id) throw new Error("Missing config id");
      const response = await ragConfigService.update(id, config);
      return ensureOk(response, "update");
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["rag-config-list", data.cmsHospitalGroupId],
      });
      queryClient.invalidateQueries({ queryKey: ["rag-config", id] });
      invalidatePatientListCaches(queryClient);
    },
  });
}
